import FootnoteSuperscript from '@deckManagement/components/Page/Footnotes/FootnoteSuperscript';
import { CustomHeaderProps } from 'ag-grid-react';

/**
 * Params passed to {@link FootnoteHeaderRenderer} via a column's `headerComponentParams`.
 */
export interface FootnoteHeaderParams {
  /**
   * Footnote ordinals attached to this header node (GROUP band, BLOCK band, or LEAF column),
   * rendered as superscript markers after the header label.
   */
  footnotes?: (number | null)[] | null;
  /**
   * Invoked when the footnote marker is clicked, with this node's footnotes and a display label
   * (the header text). Wired by `useHeaderTreeColumns` to drive the footnotes pane, mirroring
   * deckline selection. When omitted, the marker is non-interactive.
   */
  onFootnoteClick?: (label: string, footnotes: number[]) => void;
}

/**
 * Header renderer for headerTree (EE/HCTR) grid columns: the header label followed by any
 * footnote ordinal markers, via the shared {@link FootnoteSuperscript} (the one DOM footnote
 * renderer used across KPI-name cells, headers, and — soon — cells).
 *
 * When `onFootnoteClick` is provided, the footnote marker is clickable and reports this header's
 * label + footnotes so the footnotes pane can filter to them (mirroring deckline selection).
 */
const FootnoteHeaderRenderer = ({
  displayName,
  footnotes,
  onFootnoteClick,
}: CustomHeaderProps & FootnoteHeaderParams) => {
  const handleClick = onFootnoteClick
    ? () =>
        onFootnoteClick(
          displayName ?? '',
          (footnotes ?? []).filter((footnote): footnote is number => footnote != null),
        )
    : undefined;

  return (
    <span className='header-tree-footnote-header'>
      {displayName}
      <FootnoteSuperscript footnotes={footnotes} onClick={handleClick} />
    </span>
  );
};

export default FootnoteHeaderRenderer;
